/**
 * Reward Calculation Utilities
 *
 * Handles all mathematical calculations for mining rewards:
 * - SOL distribution among winners
 * - MINER token distribution
 * - Motherlode calculations
 * - Win rate and ROI statistics
 *
 * This module is pure functions only - no state, no side effects.
 * Easy to test and modify independently.
 */

import { MINING_CONFIG } from '../config/constants';

/**
 * Calculate SOL rewards for winners
 *
 * Winners split the SOL from losing blocks proportionally based on
 * how much SOL they deployed on winning blocks.
 *
 * @param {number} winningBlock - The winning block number
 * @param {Object} blockDeployments - Map of blockNum -> SOL amount
 * @param {number} totalPoolSOL - Total SOL in the round
 * @returns {Object} { winnerReward, loserLoss, totalWinnerSOL }
 */
export function calculateSOLRewards(winningBlock, blockDeployments, totalPoolSOL) {
    // Get SOL on winning block
    const winningBlockSOL = blockDeployments[winningBlock] || 0;

    if (winningBlockSOL === 0) {
        return { winnerReward: 0, loserLoss: 0, totalWinnerSOL: 0 };
    }

    // Calculate total SOL on losing blocks
    const losingBlocksSOL = totalPoolSOL - winningBlockSOL;

    // Winners get their original SOL back plus proportional share of losing SOL
    const winnerReward = winningBlockSOL + losingBlocksSOL;

    return {
        winnerReward,
        loserLoss: losingBlocksSOL,
        totalWinnerSOL: winningBlockSOL,
    };
}

/**
 * Calculate MINER token distribution
 *
 * 1 MINER token per round is distributed to winners
 * proportionally based on their winning block deployments.
 *
 * @param {number} userWinningSOL - SOL user deployed on winning block
 * @param {number} totalWinningSOL - Total SOL on winning block
 * @param {boolean} motherlodeHit - Whether motherlode was hit
 * @param {number} motherlodePot - Current motherlode jackpot amount
 * @returns {Object} { minerTokens, motherlodeReward }
 */
export function calculateMINERRewards(
    userWinningSOL,
    totalWinningSOL,
    motherlodeHit = false,
    motherlodePot = 0
) {
    if (totalWinningSOL === 0 || userWinningSOL === 0) {
        return { minerTokens: 0, motherlodeReward: 0 };
    }

    // Calculate proportional share of 1 MINER
    const proportion = userWinningSOL / totalWinningSOL;
    const minerTokens = MINING_CONFIG.TOKENS_PER_ROUND * proportion;

    // Motherlode reward (if hit)
    const motherlodeReward = motherlodeHit ? motherlodePot * proportion : 0;

    return {
        minerTokens,
        motherlodeReward,
    };
}

/**
 * Calculate claiming fee (10% of unrefined MINER)
 *
 * @param {number} unrefinedAmount - Amount of unrefined MINER
 * @returns {Object} { claimable, fee }
 */
export function calculateClaimingFee(unrefinedAmount) {
    const feePercent = MINING_CONFIG.CLAIMING_FEE_PERCENT / 100;
    const fee = unrefinedAmount * feePercent;
    const claimable = unrefinedAmount - fee;

    return {
        claimable,
        fee,
    };
}

/**
 * Calculate win rate statistics
 *
 * @param {Array} roundHistory - Array of round results
 * @returns {Object} { winRate, totalRounds, totalWins }
 */
export function calculateWinRate(roundHistory) {
    if (!roundHistory || roundHistory.length === 0) {
        return { winRate: 0, totalRounds: 0, totalWins: 0 };
    }

    const totalRounds = roundHistory.length;
    const totalWins = roundHistory.filter(round => round.won).length;
    const winRate = (totalWins / totalRounds) * 100;

    return {
        winRate,
        totalRounds,
        totalWins,
    };
}

/**
 * Calculate ROI (Return on Investment)
 *
 * @param {number} totalInvested - Total SOL invested
 * @param {number} totalReturned - Total SOL + MINER value returned
 * @returns {number} ROI percentage
 */
export function calculateROI(totalInvested, totalReturned) {
    if (totalInvested === 0) {
        return 0;
    }

    const roi = ((totalReturned - totalInvested) / totalInvested) * 100;
    return roi;
}

/**
 * Calculate total value of rewards
 *
 * @param {number} solAmount - SOL amount
 * @param {number} minerAmount - MINER token amount
 * @param {number} minerPrice - MINER price in SOL
 * @returns {number} Total value in SOL
 */
export function calculateTotalValue(solAmount, minerAmount, minerPrice = 0.1) {
    return solAmount + (minerAmount * minerPrice);
}

/**
 * Update motherlode pot
 *
 * Adds 0.2 MINER every round, resets when won
 *
 * @param {number} currentPot - Current motherlode amount
 * @param {boolean} wasWon - Whether it was won this round
 * @returns {number} New motherlode amount
 */
export function updateMotherlodePot(currentPot, wasWon) {
    if (wasWon) {
        return MINING_CONFIG.MOTHERLODE_INCREMENT; // Reset to base increment
    }
    return currentPot + MINING_CONFIG.MOTHERLODE_INCREMENT;
}

/**
 * Calculate expected value of a deployment
 *
 * @param {number} deploymentAmount - SOL to deploy
 * @param {number} numBlocks - Number of blocks deploying to
 * @param {number} totalPoolSOL - Estimated total pool SOL
 * @returns {number} Expected value in SOL
 */
export function calculateExpectedValue(deploymentAmount, numBlocks, totalPoolSOL) {
    // Probability of winning = numBlocks / 25
    const winProbability = numBlocks / MINING_CONFIG.TOTAL_BLOCKS;

    // Expected SOL return
    const expectedSOL = winProbability * totalPoolSOL;

    // Expected MINER value (assuming 0.1 SOL per MINER)
    const expectedMINER = winProbability * MINING_CONFIG.TOKENS_PER_ROUND * 0.1;

    return expectedSOL + expectedMINER;
}
